import React, { useState } from 'react';
import { Row, Col, Button, Card, Space, Modal, Alert, Collapse } from 'antd';
import { DownloadOutlined, DeleteOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import * as XLSX from 'xlsx';
import moment from 'moment';
import { postApi } from 'redux/sagas/postApiDataSaga';
import { API_STATUS, DATE_FORMAT, NOTIFICATION_STATUS_TYPES } from 'constants/app-constants';
import { showToast } from 'helpers';
import { HEADER_MAPPING, REQUIRED_COLUMNS } from './constants';
import { validateCell, getGroupedErrors } from './validationUtils';
import { downloadSampleExcel } from './excelUtils';
import UploadDragger from './UploadDragger';
import SuccessAlert from './SuccessAlert';
import ErrorDisplay from './ErrorDisplay';
import PreviewModal from './PreviewModal';
import ReferenceData from './ReferenceData';
import { useI18n } from 'hooks/useI18n';
import './style.scss';

const { Panel } = Collapse;

const BulkUpload = () => {
	const dispatch = useDispatch();
	const globalRedux = useSelector((state) => state.globalRedux);
	const branchId = globalRedux?.selectedBranch?.id;
	const { t } = useI18n();

	const [fileList, setFileList] = useState([]);
	const [validationErrors, setValidationErrors] = useState([]);
	const [validData, setValidData] = useState([]);
	const [isValidated, setIsValidated] = useState(false);
	const [uploading, setUploading] = useState(false);
	const [showPreviewModal, setShowPreviewModal] = useState(false);

	const handleFileUpload = (file) => {
		const reader = new FileReader();

		reader.onload = (e) => {
			try {
				const data = new Uint8Array(e.target.result);
				const workbook = XLSX.read(data, { type: 'array' });
				const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
				const jsonData = XLSX.utils.sheet_to_json(firstSheet);

				if (jsonData.length === 0) {
					showToast(
						t('bulk_upload.errors.excel_empty_title') || 'Error',
						t('bulk_upload.errors.excel_empty'),
						NOTIFICATION_STATUS_TYPES.ERROR
					);
					return;
				}

				// Normalize headers using mapping
				const normalizedData = jsonData.map((row) => {
					const normalizedRow = {};
					Object.keys(row).forEach((excelHeader) => {
						const fieldName = HEADER_MAPPING[excelHeader] || excelHeader;
						normalizedRow[fieldName] = row[excelHeader];
					});
					return normalizedRow;
				});

				// Auto-calculate balance if not provided
				normalizedData.forEach((row) => {
					if (row.amount !== undefined && row.paid !== undefined && !row.balance) {
						row.balance = Number(row.amount) - Number(row.paid);
					}
				});

				// Validate data
				const errors = [];
				const validRows = [];

				normalizedData.forEach((row, index) => {
					const rowNumber = index + 2;
					let hasError = false;

					// Check required columns
					REQUIRED_COLUMNS.forEach((column) => {
						const value = row[column];
						const isEmpty = value === null || value === undefined || String(value).trim() === '';

						if (isEmpty) {
							errors.push({
								row: rowNumber,
								column,
								value,
								error: `${column} is required`,
							});
							hasError = true;
						}
					});

					// Validate all columns
					Object.keys(row).forEach((column) => {
						const error = validateCell(column, row[column], row);
						if (error) {
							errors.push({
								row: rowNumber,
								column,
								value: row[column],
								error,
							});
							hasError = true;
						}
					});

					if (!hasError) {
						validRows.push(row);
					}
				});

				setValidationErrors(errors);
				setValidData(validRows);
				setIsValidated(true);

				if (errors.length === 0) {
					showToast(
						t('common.success'),
						t('bulk_upload.validated_success', { count: validRows.length }),
						NOTIFICATION_STATUS_TYPES.SUCCESS
					);
				} else {
					showToast(
						t('common.warning') || 'Warning',
						t('bulk_upload.found_validation_errors', { count: errors.length }),
						NOTIFICATION_STATUS_TYPES.WARNING
					);
				}
			} catch (error) {
				console.error('Error parsing Excel:', error);
				showToast(t('common.error') || 'Error', t('bulk_upload.errors.parse_failed'), NOTIFICATION_STATUS_TYPES.ERROR);
			}
		};

		reader.readAsArrayBuffer(file);
		return false;
	};

	const uploadProps = {
		name: 'file',
		multiple: false,
		accept: '.xlsx,.xls',
		fileList,
		beforeUpload: handleFileUpload,
		onRemove: () => {
			setFileList([]);
			setValidationErrors([]);
			setValidData([]);
			setIsValidated(false);
		},
		onChange: (info) => {
			let newFileList = [...info.fileList];
			newFileList = newFileList.slice(-1);
			setFileList(newFileList);
		},
	};

	const handleClearAll = () => {
		Modal.confirm({
			title: t('bulk_upload.actions.clear_confirm_title'),
			content: t('bulk_upload.actions.clear_confirm_content'),
			okText: t('bulk_upload.actions.clear_confirm_ok') || 'Yes, Clear',
			okType: 'danger',
			cancelText: t('common.cancel'),
			onOk: () => {
				setFileList([]);
				setValidationErrors([]);
				setValidData([]);
				setIsValidated(false);
				showToast(t('common.success'), t('bulk_upload.cleared_message'), NOTIFICATION_STATUS_TYPES.SUCCESS);
			},
		});
	};

	const handleSubmit = () => {
		if (validData.length === 0) {
			showToast('Error', 'No valid data to upload', NOTIFICATION_STATUS_TYPES.ERROR);
			return;
		}
		setShowPreviewModal(true);
	};

	const handleFinalSubmit = async () => {
		setShowPreviewModal(false);
		// setUploading(true);

		const customersData = validData.map((row) => {
			// Parse dates from DD-MM-YYYY format and convert to ISO string with current time
			const dobDate = moment(row.dob, DATE_FORMAT.DD_MM_YYYY).startOf('day');
			const joiningDate = moment(row.joiningDate, DATE_FORMAT.DD_MM_YYYY);
			const paidOnDate = moment(row.paidOn, DATE_FORMAT.DD_MM_YYYY);
			const nextBillDate = moment(row.nextBillDate, DATE_FORMAT.DD_MM_YYYY);

			return {
				customerName: row.customerName,
				displayName: row.displayName || '',
				age: Number(row.age) || moment().diff(dobDate, 'years'),
				dob: dobDate.toISOString(),
				weight: Number(row.weight),
				height: Number(row.height),
				occupation: row.occupation || '',
				email: row.email || '',
				mobile: row.mobile,
				aadhaarNo: row.aadhaarNo || '',
				address: row.address || '',
				gender: row.gender.toLowerCase(),
				joiningDate: joiningDate.toISOString(),
				referredBy: row.referredBy || '',
				onBoardedBy: row.onBoardedBy,
				image: [{ url: '' }],
				branchId,
				serviceId: row.serviceId,
				membershipId: row.membershipId,
				amount: Number(row.amount),
				paid: Number(row.paid),
				balance: Number(row.balance),
				role: 'customer',
				paymentMethod: row.paymentMethod.toLowerCase(),
				paidOn: paidOnDate.toISOString(),
				nextBillDate: nextBillDate.toISOString(),
				remarks: row.remarks || '',
				upiTransactionId: row.upiTransactionId || '',
				visitorId: null,
			};
		});

		dispatch(postApi({ customers: customersData, branchId }, 'BULK_UPLOAD_CUSTOMERS'));
		console.log('customersData', customersData);

		const checkStatus = setInterval(() => {
			if (globalRedux.apiStatus.BULK_UPLOAD_CUSTOMERS === API_STATUS.SUCCESS) {
				clearInterval(checkStatus);
				setUploading(false);
				showToast('Success', `Successfully uploaded ${customersData.length} customers!`, NOTIFICATION_STATUS_TYPES.SUCCESS);
				setFileList([]);
				setValidationErrors([]);
				setValidData([]);
				setIsValidated(false);
			} else if (globalRedux.apiStatus.BULK_UPLOAD_CUSTOMERS === API_STATUS.ERROR) {
				clearInterval(checkStatus);
				setUploading(false);
				showToast('Error', 'Failed to upload customers', NOTIFICATION_STATUS_TYPES.ERROR);
			}
		}, 500);

		setTimeout(() => {
			clearInterval(checkStatus);
			if (uploading) {
				setUploading(false);
			}
		}, 30000);
	};

	const groupedErrors = getGroupedErrors(validationErrors);
	const errorRows = Object.keys(groupedErrors).sort((a, b) => Number(a) - Number(b));

	return (
		<div className="bulk-upload-container">
			<Row gutter={[16, 16]}>
				{/* Reference Data Section */}
				<Col span={24}>
					<Collapse defaultActiveKey={[]} expandIconPosition="end" style={{ marginBottom: 16 }}>
						<Panel
							header={
								<Space>
									<InfoCircleOutlined style={{ fontSize: 18, color: '#1890ff' }} />
									<span style={{ fontSize: 16, fontWeight: 600 }}>{t('bulk_upload.reference.header')}</span>
								</Space>
							}
							key="1">
							<ReferenceData />
						</Panel>
					</Collapse>
				</Col>

				<Col span={24}>
					<Card
						bordered={false}
						title={<span style={{ fontSize: 18, fontWeight: 600 }}>{t('bulk_upload.title')}</span>}
						extra={
							<Space>
								<Button type="primary" icon={<DownloadOutlined />} onClick={downloadSampleExcel} size="large">
									{t('bulk_upload.actions.download_template')}
								</Button>
								{(isValidated || fileList.length > 0) && (
									<Button danger icon={<DeleteOutlined />} onClick={handleClearAll} size="large">
										{t('bulk_upload.actions.clear_all')}
									</Button>
								)}
							</Space>
						}>
						<Space direction="vertical" size="large" style={{ width: '100%' }}>
							<Alert
								message={t('bulk_upload.instructions.title')}
								description={
									<ul style={{ margin: '8px 0', paddingLeft: 20 }}>
										<li>{t('bulk_upload.instructions.list.download_template')}</li>
										<li>
											<strong>{t('bulk_upload.instructions.list.need_ids_label')}</strong>{' '}
											{t('bulk_upload.instructions.list.need_ids')}
										</li>
										<li>
											<strong>{t('bulk_upload.instructions.list.required_fields_label')}</strong>{' '}
											{t('bulk_upload.instructions.list.required_fields')}
										</li>
										<li>
											<strong>{t('bulk_upload.instructions.list.important_label')}</strong>{' '}
											{t('bulk_upload.instructions.list.upi_mandatory')}
										</li>
										<li>
											<strong>{t('bulk_upload.instructions.list.date_format_label')}</strong>{' '}
											{t('bulk_upload.instructions.list.date_format')}
										</li>
										<li>
											<strong>{t('bulk_upload.instructions.list.upload_label')}</strong>{' '}
											{t('bulk_upload.instructions.list.upload_instruction')}
										</li>
									</ul>
								}
								type="info"
								showIcon
								style={{ marginBottom: 16 }}
							/>

							<UploadDragger uploadProps={uploadProps} fileList={fileList} />

							{isValidated && validationErrors.length === 0 && validData.length > 0 && (
								<SuccessAlert validDataCount={validData.length} onPreview={handleSubmit} uploading={uploading} />
							)}

							{validationErrors.length > 0 && (
								<ErrorDisplay validationErrors={validationErrors} groupedErrors={groupedErrors} errorRows={errorRows} />
							)}
						</Space>
					</Card>
				</Col>
			</Row>

			<PreviewModal
				visible={showPreviewModal}
				onCancel={() => setShowPreviewModal(false)}
				onConfirm={handleFinalSubmit}
				validData={validData}
				uploading={uploading}
			/>
		</div>
	);
};

export default BulkUpload;
